// App-lock secret policy (Tool Plan §4.14 step 5, D-77; Implementation Plan v02
// §6.2, §9; I-1a Batch 4, IP2-29 (3)).
//
// - A passphrase is recommended: at least 10 characters (Aldo: "10 minimum").
// - Digits only: 8 or more are allowed; a 6-digit PIN is allowed, with its honest
//   limit shown at the moment of choice (the screen does that, not this file).
// - Refused, with a calm line: anything shorter (and 7 digits), repeats and
//   sequences, common PINs (the built-in list below), and dates in any
//   day / month / year order in both 6- and 8-digit forms.
//
// Pure: no storage, no crypto. The lock session checks again before it derives a
// key, so a screen bug cannot set a refused secret.

export const PASSPHRASE_MIN_LENGTH = 10
export const DIGITS_MIN_LENGTH = 8
export const PIN_LENGTH = 6

/** passphrase = has a non-digit; digits = 8 or more digits; pin6 = exactly 6 digits. */
export type SecretKind = 'passphrase' | 'digits' | 'pin6'

export type SecretRefusal = 'too-short' | 'digits-length' | 'repeated' | 'sequence' | 'common' | 'date'

export type SecretCheck =
  | { readonly ok: true; readonly kind: SecretKind }
  | { readonly ok: false; readonly refusal: SecretRefusal }

// Common PINs: the most-chosen 6- and 8-digit PINs in published PIN-frequency
// studies, plus keypad shapes (rows, columns, diagonals, crosses). Repeats,
// runs and dates are caught by rule, so they are not listed again here.
const COMMON_PINS: ReadonlySet<string> = new Set([
  // 6 digits
  '112233', '123321', '123654', '123789', '102030', '100200', '101010', '110110',
  '111222', '121314', '123412', '131313', '147258', '147369', '147852', '159357',
  '159753', '246810', '258456', '258258', '321654', '369258', '456123', '456789',
  '520520', '520131', '654123', '696969', '741852', '741963', '753159', '789456',
  '789123', '852456', '852963', '951753', '963852', '987456', '135790', '135791',
  '246802', '007007', '000111', '110011', '001100', '199999',
  // 8 digits
  '11223344', '12344321', '13579246', '14725836', '14789632', '15935748', '20202020',
  '24682468', '25802580', '12369874', '74108520', '96385274', '11112222', '12121234',
  '13131313', '69696969', '19999999', '12345679', '98765431',
])

// Common long passphrases (lower case, spaces removed). Short, on purpose: the
// 10-character minimum, repeats and runs do most of the work.
const COMMON_PASSPHRASES: ReadonlySet<string> = new Set([
  'password12', 'password123', 'password1234', 'passw0rd12', 'qwertyuiop', 'qwerty1234',
  'qwerty12345', '1q2w3e4r5t', 'q1w2e3r4t5', '1qaz2wsx3edc', 'asdfghjkl;', 'zxcvbnm123',
  'iloveyou12', 'iloveyou123', 'abc1234567', 'letmein123', 'welcome123',
])

// Keyboard rows, checked as runs (forward and back) like the alphabet.
const RUN_SOURCES: readonly string[] = ['abcdefghijklmnopqrstuvwxyz', 'qwertyuiop', 'asdfghjkl', 'zxcvbnm']

function isAllDigits(s: string): boolean {
  return /^[0-9]+$/.test(s)
}

/** The whole string is one shorter block said again (111111, 121212, 123123, abcabcabcabc). */
function isRepeated(chars: readonly string[]): boolean {
  const n = chars.length
  for (let len = 1; len <= n / 2; len++) {
    if (n % len !== 0) {
      continue
    }
    let same = true
    for (let i = len; i < n && same; i++) {
      same = chars[i] === chars[i - len]
    }
    if (same) {
      return true
    }
  }
  return false
}

/** Digits stepping by +1 or −1 all the way (wrapping 9 ↔ 0): 123456, 654321, 7890123. */
function isDigitRun(s: string): boolean {
  if (s.length < 3) {
    return false
  }
  for (const step of [1, 9]) {
    let run = true
    for (let i = 1; i < s.length && run; i++) {
      run = (Number(s[i - 1]) + step) % 10 === Number(s[i])
    }
    if (run) {
      return true
    }
  }
  return false
}

/** The whole (lower-cased) text is a stretch of the alphabet or a keyboard row, either way. */
function isLetterRun(lower: string): boolean {
  return RUN_SOURCES.some((source) => {
    const back = [...source].reverse().join('')
    return source.includes(lower) || back.includes(lower)
  })
}

function daysInMonth(month: number, year: number | null): number {
  if (month === 2) {
    // A 2-digit year could be any century, so 29 February always counts.
    const leap = year === null || (year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0))
    return leap ? 29 : 28
  }
  return [4, 6, 9, 11].includes(month) ? 30 : 31
}

function isRealDate(day: number, month: number, year: number | null): boolean {
  return month >= 1 && month <= 12 && day >= 1 && day <= daysInMonth(month, year)
}

type Part = 'd' | 'm' | 'y'
const ORDERS: readonly (readonly [Part, Part, Part])[] = [
  ['d', 'm', 'y'],
  ['m', 'd', 'y'],
  ['y', 'm', 'd'],
  ['y', 'd', 'm'],
  ['d', 'y', 'm'],
  ['m', 'y', 'd'],
]

/** A date in any day / month / year order: 6 digits (2-digit year) or 8 digits (4-digit year 1900–2099). */
export function looksLikeDate(digits: string): boolean {
  if (!isAllDigits(digits) || (digits.length !== 6 && digits.length !== 8)) {
    return false
  }
  const yearLength = digits.length === 8 ? 4 : 2
  return ORDERS.some((order) => {
    const values: Partial<Record<Part, number>> = {}
    let at = 0
    for (const part of order) {
      const len = part === 'y' ? yearLength : 2
      values[part] = Number(digits.slice(at, at + len))
      at += len
    }
    const year = values.y ?? 0
    if (yearLength === 4 && (year < 1900 || year > 2099)) {
      return false
    }
    return isRealDate(values.d ?? 0, values.m ?? 0, yearLength === 4 ? year : null)
  })
}

/** Check a new app-lock secret. Characters are counted as the person sees them (code points, NFC). */
export function checkSecret(secret: string): SecretCheck {
  const text = secret.normalize('NFC')
  const chars = [...text]

  if (text.length > 0 && isAllDigits(text)) {
    if (chars.length < PIN_LENGTH) {
      return { ok: false, refusal: 'too-short' }
    }
    if (chars.length !== PIN_LENGTH && chars.length < DIGITS_MIN_LENGTH) {
      return { ok: false, refusal: 'digits-length' }
    }
    if (isRepeated(chars)) {
      return { ok: false, refusal: 'repeated' }
    }
    if (isDigitRun(text)) {
      return { ok: false, refusal: 'sequence' }
    }
    if (COMMON_PINS.has(text)) {
      return { ok: false, refusal: 'common' }
    }
    if (looksLikeDate(text)) {
      return { ok: false, refusal: 'date' }
    }
    return { ok: true, kind: chars.length === PIN_LENGTH ? 'pin6' : 'digits' }
  }

  if (chars.length < PASSPHRASE_MIN_LENGTH) {
    return { ok: false, refusal: 'too-short' }
  }
  const lower = text.toLowerCase()
  if (isRepeated([...lower])) {
    return { ok: false, refusal: 'repeated' }
  }
  if (isLetterRun(lower)) {
    return { ok: false, refusal: 'sequence' }
  }
  if (COMMON_PASSPHRASES.has(lower.replace(/\s+/g, ''))) {
    return { ok: false, refusal: 'common' }
  }
  return { ok: true, kind: 'passphrase' }
}
